import React, { useEffect, useState, useMemo } from "react";
import { useGlobalContext } from "../contextProvider/ContextProvider";
import TopNavigation from "./TopNavigation";
import BottomNavigation from "./BottomNavigation";
import general from "../styles/General.module.scss";
import Submitted from "./Submitted";
import Steps from "./Steps";

const Configurator = () => {
  const {
    configuratorId,
    currentStep,
    totalDynamicSteps,
    inputSummary,
    setInputSummary,
    currentStepDescription,
    setCurrentStepDescription,
    submitted,
    totalSteps,
    setTotalSteps,
    setTotalDynamicSteps,
    setProgressPercentage,
  } = useGlobalContext();

  const [image, setImage] = useState("");
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(true);

  const [currentTitle, setCurrentTitle] = useState("");
  const [previousStepTitle, setPreviousStepTitle] = useState("");
  const [nextStepTitle, setNextStepTitle] = useState("");

  const currentComponent = useMemo(() => {
    if (submitted) return "Submitted";
    if (currentStep === totalDynamicSteps + 4) return "Rechnungsadresse";
    if (currentStep === totalDynamicSteps + 3) return "Sonstiges";
    if (currentStep === totalDynamicSteps + 2) return "Baustellenadresse";
    if (currentStep === totalDynamicSteps + 1) return "Wunschtermin";
    if (currentStep <= totalDynamicSteps) return "Inputs";
    return null;
  }, [currentStep, submitted, totalDynamicSteps]);

  const handleInputChange = (name, value, type, checked, single) => {
    // edge case for single checkbox
    if (single) {
      setInputSummary((prevState) => {
        const updatedSummary = { ...prevState };

        if (name === "wieBaustellenadresse") {
          if (checked) {
            delete updatedSummary["strasse-rechnungsadresse"];
            delete updatedSummary["plz-rechnungsadresse"];
            delete updatedSummary["ort-rechnungsadresse"];
          } else {
            updatedSummary["strasse-rechnungsadresse"] =
              updatedSummary["strasse-baustellenadresse"];
            updatedSummary["plz-rechnungsadresse"] =
              updatedSummary["plz-baustellenadresse"];
            updatedSummary["ort-rechnungsadresse"] =
              updatedSummary["ort-baustellenadresse"];
          }
        }

        return {
          ...updatedSummary,
          [name]: checked,
        };
      });
      return;
    }

    if (type === "radio") {
      setInputSummary((prevState) => ({
        ...prevState,
        [name]: value, // For radio buttons, set the value directly
      }));
    } else if (type === "checkbox") {
      setInputSummary((prevState) => {
        const currentValues = prevState[name] || [];
        const updatedValues = checked
          ? [...currentValues, value]
          : currentValues.filter((item) => item !== value);
        return {
          ...prevState,
          [name]: updatedValues,
        };
      });
    } else if (type === "file") {
      setInputSummary((prevState) => ({
        ...prevState,
        [name]: value, // Store the file object
      }));
    } else {
      setInputSummary((prevState) => ({
        ...prevState,
        [name]: value, // For other input types, set the value directly
      }));
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API}/api/steps/${configuratorId}`)
      .then((response) => response.json())
      .then((data) => {
        // Set image if available
        if (
          currentStep <= totalDynamicSteps &&
          data[currentStep - 1].image_path
        ) {
          setImage(data[currentStep - 1].image_path);
        } else {
          setImage("");
        }

        // Update TopNavigation
        if (currentStep <= totalDynamicSteps) {
          setCurrentTitle(data[currentStep - 1].title);
          setPreviousStepTitle(data[currentStep - 2]?.title || "");
          setNextStepTitle(data[currentStep]?.title || "Wunschtermin");
          setProgressPercentage((currentStep / (data.length + 4)) * 100);
          // update step description
          setCurrentStepDescription(data[currentStep - 1].description);
        } else if (currentStep === totalDynamicSteps + 1) {
          setCurrentTitle("Wunschtermin");
          setPreviousStepTitle(data[currentStep - 2]?.title || "");
          setNextStepTitle("Baustellenadresse");
          setProgressPercentage((currentStep / (data.length + 4)) * 100);
          // update step description
          setCurrentStepDescription("");
        } else if (currentStep === totalDynamicSteps + 2) {
          setCurrentTitle("Baustellenadresse");
          setPreviousStepTitle("Wunschtermin");
          setNextStepTitle("Sonstiges");
          setProgressPercentage((currentStep / (data.length + 4)) * 100);
        } else if (currentStep === totalDynamicSteps + 3) {
          setCurrentTitle("Sonstiges");
          setPreviousStepTitle("Baustellenadresse");
          setNextStepTitle("Rechnungsadresse");
          setProgressPercentage((currentStep / (data.length + 4)) * 100);
        } else if (currentStep === totalDynamicSteps + 4) {
          setCurrentTitle("Rechnungsadresse");
          setPreviousStepTitle("Sonstiges");
          setNextStepTitle("");
          setProgressPercentage(100);
        }

        if (currentStep <= totalDynamicSteps) {
          const parsedInputs = JSON.parse(data[currentStep - 1].inputs);
          setInputs(parsedInputs);

          // Initialize inputSummary with default values for checkboxes and radio buttons
          const initialInputSummary = {};
          Object.keys(parsedInputs).forEach((key, index) => {
            const input = parsedInputs[key];
            const category = input["category" + index];
            const labelValue = input["label" + index];
            const [label, name] = labelValue.includes("::")
              ? labelValue.split("::")
              : [labelValue, labelValue];
            if (category === "auswahl") {
              const multipleOption = input["multipleOption" + index];
              if (multipleOption === "mehrfachauswahl") {
                initialInputSummary[name] = [];
              } else if (multipleOption === "einzelauswahl") {
                initialInputSummary[name] = ""; // Initialize with an empty string for radio buttons
              }
            }
          });

          setInputSummary((prevState) => ({
            ...prevState,
            ...Object.fromEntries(
              Object.entries(initialInputSummary).map(([key, value]) => [
                key,
                prevState[key] || value,
              ])
            ),
          }));
        }
        setTotalSteps(data.length + 4);
        setTotalDynamicSteps(data.length);
        setLoading(false);
      })
      .catch((error) => console.error(error));
  }, [
    currentStep,
    configuratorId,
    totalDynamicSteps,
    setInputSummary,
    totalSteps,
    setCurrentStepDescription,
    setTotalSteps,
    setProgressPercentage,
    setTotalDynamicSteps,
  ]);

  // additional infos
  // useEffect(() => {
  //   setTimeout(() => {
  //     const flächeInput = document.querySelector('input[name="Fläche"]');

  //     if (flächeInput) {
  //       flächeInput.placeholder = "in Quadratmetern angeben";
  //     }
  //   }, 200);
  // }, [currentStep]);

  if (!loading) {
    return (
      <div style={{ width: "100%" }}>
        <TopNavigation
          loading={loading}
          previousStepTitle={previousStepTitle}
          currentTitle={currentTitle}
          nextStepTitle={nextStepTitle}
        />

        <div className={general.configuratorContainer}>
          {image && !submitted ? (
            <img
              src={`${process.env.REACT_APP_API}/images/${image}`}
              alt={`Bild für ${currentStep}`}
              className={general.configuratorImage}
            />
          ) : null}
          {!submitted ? (
            <div className={general.configuratorInputs}>
              {currentStepDescription !== "" ? (
                <p style={{ marginBottom: "50px" }}>{currentStepDescription}</p>
              ) : null}
              <Steps
                component={currentComponent}
                inputs={inputs}
                handleInputChange={handleInputChange}
                inputSummary={inputSummary}
                currentStepDescription={currentStepDescription}
              />
            </div>
          ) : null}
          {currentComponent === "Submitted" && <Submitted />}
        </div>
        <BottomNavigation currentComponent={currentComponent} />
      </div>
    );
  }
};

export default Configurator;
